import { FunctionComponent, useRef } from "react";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import logo from "images/colour-light-background.svg";
import { IProps } from "./login.types";
import Version from "./version";
import "./login.scss";
import imageUrl from "./background-cover-image.jpg";
import { useAuthMethods } from "hooks-for/auth/use-auth-methods";
import { hotToastOperations } from "utilities-for/toast";

/**
 * Sign in page. The backend provides a list of possible authentication providers (Salesforce, Zendesk etc.) that are
 * presented to the user.
 * @returns Application version number and sign in button(s).
 */
const Login: FunctionComponent<IProps> = () => {
    const baseDelay = useRef(1000); // initial base delay in ms
    const { data: authMethods } = useAuthMethods(
        {},
        {
            retry: true,
            retryDelay: (_failureCount, error) => {
                hotToastOperations.showServerErrorToast("Failed to request authentication options", error, {
                    id: "authMethods-error",
                });
                // Calculate a random jitter to cause variation in delay. Increase delay by 40% - 80% compared to previous
                const jitter = Math.random() * (0.8 - 0.4) + 0.4;

                baseDelay.current = Math.min(30_000, baseDelay.current * (1 + jitter));
                return baseDelay.current;
            },
        }
    );

    /**
     * Production will transform this whole function into url => url
     * This is so the development server can log in via an SSO provider but it will not impact production
     * @param url
     */
    const rewriteLoginUrl = (url: string) => {
        if (process.env.NODE_ENV === "development") {
            const redirectMatch = decodeURIComponent(url).match(/redirect_uri=(.+)\/authenticate/);
            if (redirectMatch === null || typeof redirectMatch[1] === "undefined") return url;

            return url.replace(redirectMatch[1], window.location.origin);
        } else {
            return url;
        }
    };

    return (
        <>
            <div className="login__background" style={{ backgroundImage: `url(${imageUrl})` }} />
            <div className="login">
                <img src={logo} alt="EdgeTier" />
                <h1>Arthur</h1>
                {!Array.isArray(authMethods) ? (
                    <div className="login__loading">
                        <FontAwesomeIcon icon={faSpinner} spin={true} />
                    </div>
                ) : (
                    authMethods.map(({ displayName, loginUrl }) => (
                        <a className="button" href={rewriteLoginUrl(loginUrl)} key={displayName}>
                            Sign in with {displayName}
                        </a>
                    ))
                )}
                <Version />
            </div>
        </>
    );
};

export default Login;
